import { useState } from "react";

import type {
  DownloadBanner,
  GetLanguageLabel,
  MarketingBannerRow,
  MarketingBannersTranslations,
} from "./types";

import { CopyButton } from "../CopyButton";
import { ViewPerformanceButton } from "../ViewPerformanceButton";
import { BannerMoreOptionsPopoverMenu } from "./BannerMoreOptionsPopoverMenu";
import { BannerPreviewModal } from "./BannerPreviewModal";
import { BannerTooltipText } from "./Cells";
import { CreativePreview } from "./CreativePreview";

const closeOpenPopover = () => {
  document.dispatchEvent(new MouseEvent("mousedown"));
};

export const BannerNameCell = ({
  banner,
  translations,
  getLanguageLabel,
  onRename,
  onDownload,
  performanceHref,
  onViewPerformance,
  onNavigateToPerformance,
  onCopy,
  onView,
  onMoreMenuCopy,
  onMenuOpen,
  onPreviewCopy,
}: {
  banner: MarketingBannerRow;
  translations: MarketingBannersTranslations;
  getLanguageLabel: GetLanguageLabel;
  onRename?: () => void;
  onDownload?: DownloadBanner;
  performanceHref?: string;
  onViewPerformance?: () => void;
  onNavigateToPerformance?: (href: string) => void;
  /** Fired before the standalone row copy icon writes to the clipboard. */
  onCopy?: () => void;
  /** Fired when the row's "View banner" preview modal is opened. */
  onView?: () => void;
  /** Fired before the more-menu's "Copy HTML code" item writes to the clipboard. */
  onMoreMenuCopy?: () => void;
  /** Fired when the row's "More options" menu is opened. */
  onMenuOpen?: () => void;
  /** Fired before the row's preview modal "Copy HTML code" action writes to the clipboard. */
  onPreviewCopy?: () => void;
}) => {
  const { copyLabel, copiedLabel } = translations.actions.getCopyLabels(
    banner.type,
  );

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleViewClick = () => {
    onView?.();
    closeOpenPopover();
    setIsPreviewOpen(true);
  };

  const handleRenameClick = () => {
    closeOpenPopover();
    onRename?.();
  };

  const handleDownloadClick = async () => {
    closeOpenPopover();
    try {
      await onDownload?.(banner);
    } catch (err) {
      console.error("BannerNameCell: failed to download banner image", err);
    }
  };

  return (
    <div className="flex h-[80px] min-w-0 items-center gap-4 p-4">
      <CreativePreview banner={banner} maxWidth={57} maxHeight={48} />
      <div className="flex min-w-0 flex-1 items-center gap-10">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <BannerTooltipText text={banner.name} tooltipText={banner.name} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <CopyButton
            id={`banner-copy-${banner.id}`}
            text={banner.creative_code}
            copyLabel={copyLabel}
            copiedLabel={copiedLabel}
            onCopy={onCopy}
          />
          {performanceHref && translations.actions.viewPerformance && (
            <ViewPerformanceButton
              id={banner.id}
              href={performanceHref}
              label={translations.actions.viewPerformance}
              onClick={onViewPerformance}
              onNavigate={onNavigateToPerformance}
            />
          )}
          <BannerMoreOptionsPopoverMenu
            banner={banner}
            translations={translations}
            onView={handleViewClick}
            onCopy={onMoreMenuCopy}
            onRename={handleRenameClick}
            onDownload={handleDownloadClick}
            onMenuOpen={onMenuOpen}
          />
        </div>
      </div>

      <BannerPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        banner={banner}
        translations={translations}
        getLanguageLabel={getLanguageLabel}
        onDownload={onDownload}
        onCopy={onPreviewCopy}
      />
    </div>
  );
};
